import { useMemo } from 'react';

import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useAdminDevStagingProdEnvs } from '@/hooks/admin/environments';
import { useEnvironmentSwitcher } from '@/hooks/useEnvironmentSwitcher';

type BadgeType =
  | 'default'
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'info-alt'
  | 'teal';

export type Environment = {
  label: string;
  badge?: string;
  path: string;
  type?: BadgeType;
};

export interface EnvironmentInfo {
  environments: Environment[];
  currentEnvironment: Environment | undefined;
  selectedEnv: string;
}

export const useEnvironments = (): EnvironmentInfo => {
  const { accountId, projectId } = useDbtCloudProject();

  const adminEnvironments = useAdminDevStagingProdEnvs({
    accountId,
    projectId,
  }).data;

  const productionEnv = adminEnvironments?.production?.id;
  const stagingEnv = adminEnvironments?.staging?.id;
  const devEnv = adminEnvironments?.development?.id;

  const {
    productionUrl,
    stagingUrl,
    devUrl,
    selectedEnv,
    allowDevEnvToggle,
    isProductionEnabled,
    isStagingEnabled,
  } = useEnvironmentSwitcher(productionEnv, stagingEnv, devEnv);

  const environments: Environment[] = useMemo(
    () => [
      ...(isProductionEnabled
        ? [
            {
              label: 'Production',
              badge: 'PROD',
              path: productionUrl,
              type: 'info' as BadgeType,
            },
          ]
        : []),
      ...(isStagingEnabled
        ? [
            {
              label: 'Staging',
              badge: 'STG',
              path: stagingUrl,
              type: 'success' as BadgeType,
            },
          ]
        : []),
      ...(allowDevEnvToggle
        ? [
            {
              label: 'Development',
              badge: 'DEV',
              path: devUrl,
              type: 'info-alt' as BadgeType,
            },
          ]
        : []),
    ],
    [
      isProductionEnabled,
      isStagingEnabled,
      allowDevEnvToggle,
      productionUrl,
      stagingUrl,
      devUrl,
    ],
  );

  const currentEnvironment = useMemo(() => {
    if (selectedEnv === 'devEnv')
      return environments.find((e) => e.label === 'Development');
    if (selectedEnv === 'stagingEnv')
      return environments.find((e) => e.label === 'Staging');
    if (selectedEnv === 'prodEnv')
      return environments.find((e) => e.label === 'Production');
    return environments[0];
  }, [environments, selectedEnv]);

  return {
    environments,
    currentEnvironment,
    selectedEnv,
  };
};
